import React from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import { UseFormRegister } from 'react-hook-form'

import { ContractSettingFormType, EmailTemplatesPropsType } from '../../types'

type EmailFieldName =
  | 'signatureRequestEmailSubject'
  | 'signatureRequestEmailBody'
  | 'declineNotificationEmailSubject'
  | 'declineNotificationEmailBody'
  | 'completionNotificationEmailSubject'
  | 'completionNotificationEmailBody'

type Placeholder = { label: string; token: string; description: string }

const CONTRACT_NAME: Placeholder = {
  label: '契約名',
  token: '{{contract_name}}',
  description: '契約書のタイトル（例: 〇〇家・××家_婚礼申込書）',
}
const INVITEE_NAME: Placeholder = {
  label: '署名者名',
  token: '{{invitee_name}}',
  description: 'クライアント情報で入力をされたお客様の氏名（例: 田中 太郎）',
}
const SIGN_URL: Placeholder = { label: '署名URL', token: '{{sign_url}}', description: '署名ページへのリンク' }
const DEADLINE: Placeholder = {
  label: '署名期限',
  token: '{{deadline}}',
  description: '署名できる最終日（発行日から30日間）',
}
const DECLINE_REASON: Placeholder = {
  label: '否認理由',
  token: '{{decline_reason}}',
  description: '署名ページで否認した人が入力した理由（例: 内容が間違っているため）',
}

type SectionProps = {
  title: string
  placeholders: Placeholder[]
  subjectName: EmailFieldName
  bodyName: EmailFieldName
  register: UseFormRegister<ContractSettingFormType>
  bodyNote?: React.ReactNode
  bodyError?: string
}

const EmailTemplateSection: React.FC<SectionProps> = ({
  title,
  placeholders,
  subjectName,
  bodyName,
  register,
  bodyNote,
  bodyError,
}) => (
  <Card className="mb-4">
    <Card.Header as="h5">{title}</Card.Header>
    <Card.Body>
      <div className="mb-3">
        <div className="small text-muted mb-1">差し込み項目（件名・本文に入力すると送信時に自動で置き換わります）</div>
        <ul className="small text-muted mb-0">
          {placeholders.map((placeholder) => (
            <li key={placeholder.token}>
              {placeholder.label}: <code>{placeholder.token}</code>：{placeholder.description}
            </li>
          ))}
        </ul>
      </div>
      <Form.Group className="mb-3">
        <Form.Label htmlFor={subjectName}>件名</Form.Label>
        <Form.Control {...register(subjectName)} id={subjectName} type="text" />
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor={bodyName}>本文</Form.Label>
        <Form.Control {...register(bodyName)} id={bodyName} as="textarea" rows={8} isInvalid={!!bodyError} />
        <Form.Control.Feedback type="invalid">{bodyError}</Form.Control.Feedback>
        {bodyNote && <Form.Text className="text-muted">{bodyNote}</Form.Text>}
      </Form.Group>
    </Card.Body>
  </Card>
)

export const EmailTemplates: React.FC<EmailTemplatesPropsType> = (props) => {
  return (
    <>
      <header className="row align-items-center justify-content-end sticky-top bg-white z-2500">
        <Col className="text-end">
          <Button
            variant="primary"
            onClick={props.submitContractSetting}
            disabled={!props.isContractSettingFormDirty || props.isSubmittingContractSetting}
          >
            保存{!props.isContractSettingFormDirty && '済み'}
            {props.isSubmittingContractSetting && '中'}
          </Button>
          {!!props.contractSetting?.id && (
            <Button variant="danger" className="ms-3" onClick={props.deleteContractSetting}>
              削除
            </Button>
          )}
        </Col>
      </header>
      <Row>
        <Col>
          <Form onSubmit={props.submitContractSetting} className="mt-3">
            <EmailTemplateSection
              title="署名依頼メール"
              placeholders={[CONTRACT_NAME, INVITEE_NAME, SIGN_URL, DEADLINE]}
              subjectName="signatureRequestEmailSubject"
              bodyName="signatureRequestEmailBody"
              register={props.contractSettingRegister}
              bodyError={props.contractSettingErrors.signatureRequestEmailBody?.message}
              bodyNote={
                <>
                  ※ <code>{SIGN_URL.token}</code>{' '}
                  は送信時に署名ページのURLに置き換わります。位置は変えて構いませんが、必ず本文に残してください。含まれていないテンプレートは保存できません。
                </>
              }
            />
            <EmailTemplateSection
              title="否認通知メール"
              placeholders={[CONTRACT_NAME, INVITEE_NAME, DECLINE_REASON]}
              subjectName="declineNotificationEmailSubject"
              bodyName="declineNotificationEmailBody"
              register={props.contractSettingRegister}
            />
            <EmailTemplateSection
              title="契約締結通知メール"
              placeholders={[CONTRACT_NAME, INVITEE_NAME]}
              subjectName="completionNotificationEmailSubject"
              bodyName="completionNotificationEmailBody"
              register={props.contractSettingRegister}
            />
          </Form>
        </Col>
      </Row>
    </>
  )
}
